import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  LinearProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Avatar,
  Grid,
  Divider,
  Button,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  PersonOutline,
  MonetizationOnOutlined,
  Wallet,
  CheckCircleOutline,
  HighlightOffOutlined,
  SupervisedUserCircle,
  StarOutline,
  BarChart,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { Download } from '@mui/icons-material'; // Pour l'icône du bouton
// Sous-composant pour la carte client
const ClientCard = ({ client: c }) => (
  <Grid item xs={12} md={6}>
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 4,
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        transition: '0.2s',
        '&:hover': { boxShadow: '0 8px 16px -4px rgba(0,0,0,0.1)' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Avatar
          sx={{ bgcolor: '#4f46e5', width: 56, height: 56, fontWeight: 'bold' }}
        >
          {c.name?.[0]}
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
            {c.name} {c.prenom}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            {c.email}
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#f0f9ff',
              border: '1px solid #e0f2fe',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#0369a1', display: 'flex', alignItems: 'center' }}
            >
              <MonetizationOnOutlined fontSize="small" sx={{ mr: 0.5 }} />{' '}
              Dépenses
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography
                sx={{ fontWeight: 800, color: '#075985', fontSize: '1.1rem' }}
              >
                {c.total_spent}€
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: '#64748b', fontWeight: 600 }}
              >
                ({c.reservations_count} résa)
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#fff7ed',
              border: '1px solid #ffedd5',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#c2410c', display: 'flex', alignItems: 'center' }}
            >
              <Wallet fontSize="small" sx={{ mr: 0.5 }} /> Panier Moyen
            </Typography>
            <Typography
              sx={{ fontWeight: 800, color: '#9a3412', fontSize: '1.1rem' }}
            >
              {c.avg_spent_per_reservation}€
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#f0fdf4',
              border: '1px solid #dcfce7',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#16a34a', display: 'flex', alignItems: 'center' }}
            >
              <CheckCircleOutline fontSize="small" sx={{ mr: 0.5 }} /> Validées
            </Typography>
            <Typography
              sx={{ fontWeight: 800, color: '#166534', fontSize: '1.1rem' }}
            >
              {c.successful_reservations}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#fef2f2',
              border: '1px solid #fee2e2',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#dc2626', display: 'flex', alignItems: 'center' }}
            >
              <HighlightOffOutlined fontSize="small" sx={{ mr: 0.5 }} />{' '}
              Annulées
            </Typography>
            <Typography
              sx={{ fontWeight: 800, color: '#991b1b', fontSize: '1.1rem' }}
            >
              {c.cancelled_reservations}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  </Grid>
);

export default function DashboardOffres({ data }) {
  const intervenant = data?.topIntervenants?.[0];
  const offers = useMemo(
    () => (Array.isArray(data) ? data : data?.offers || []),
    [data]
  );
  const [openRow, setOpenRow] = useState(null);
  const [limits, setLimits] = useState({});
  const [expandedList, setExpandedList] = useState({});
  const apiUrl = process.env.REACT_APP_API_URL;



  const handleLoadMore = (offreId) => {
    setLimits((prev) => ({ ...prev, [offreId]: (prev[offreId] || 5) + 5 }));
  };

  const toggleList = (offreId) => {
    setExpandedList((prev) => ({ ...prev, [offreId]: !prev[offreId] }));
    if (!expandedList[offreId]) {
      setLimits((prev) => ({ ...prev, [offreId]: 5 }));
    }
  };

  const handleExportAllData = (data) => {
    // On s'assure que data est un tableau (au cas où ce soit un objet unique)
    const offers = Array.isArray(data) ? data : [data];

    // 1. Préparation des données pour les 3 onglets
    const tabOffres = [];
    const tabClients = [];
    const tabReservations = [];

    offers.forEach((o) => {
      // Onglet 1 : Stats globales de l'offre
      tabOffres.push({
        Titre: o.title,
        Prix: o.price,
        Revenus: o.revenue,
        Occupancy_Rate: `${o.occupancy_rate}%`,
        Total_Reservations: o.total_reservations,
        Succes: o.successful_reservations,
        Annule: o.cancelled_reservations,
        Intervenant: `${o.intervenant?.prenom} ${o.intervenant?.nom}`,
      });

      // Onglet 2 : Détail Clients (On ajoute le titre de l'offre pour le contexte)
      o.clients?.forEach((c) => {
        tabClients.push({
          Offre: o.title,
          Nom: c.name,
          Prenom: c.prenom,
          Email: c.email,
          Depenses_Totales: c.total_spent,
          Nb_Reservations: c.reservations_count,
          Taux_Succes: `${c.success_rate}%`,
          Derniere_Reservation: c.last_reservation,
        });
      });

      // Onglet 3 : Timeline des réservations
      o.reservation_timeline?.forEach((r) => {
        tabReservations.push({
          Offre: o.title,
          Reservation_ID: r.reservation_id,
          Status: r.status,
          Date_Debut: r.start_date,
          Client: `${r.client?.prenom} ${r.client?.name}`,
        });
      });
    });

    // 2. Création du WorkBook et des WorkSheets
    const wb = XLSX.utils.book_new();

    const wsOffres = XLSX.utils.json_to_sheet(tabOffres);
    const wsClients = XLSX.utils.json_to_sheet(tabClients);
    const wsReservations = XLSX.utils.json_to_sheet(tabReservations);

    // 3. Ajout au fichier
    XLSX.utils.book_append_sheet(wb, wsOffres, 'Statistiques Offres');
    XLSX.utils.book_append_sheet(wb, wsClients, 'Liste Clients');
    XLSX.utils.book_append_sheet(wb, wsReservations, 'Historique Resa');

    // 4. Génération du fichier
    XLSX.writeFile(wb, 'Export_Dashboard_Complet.xlsx');
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, }}>
      <Box sx={{ mb: 4 }}>
        {intervenant && (
          <Stack
            direction="row"
            alignItems="center"
           
            sx={{ mt: 1, color: '#64748b' }}
          >
            <SupervisedUserCircle fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>
              Intervenant : {intervenant.prenom} {intervenant.nom}
            </Typography>
          </Stack>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
  <Button
    variant="contained"
    startIcon={<Download />}
    onClick={() => handleExportAllData(data)}
    sx={{
      textTransform: 'none',
      fontWeight: 700,
      fontSize: '0.85rem',
      bgcolor: '#10b981',
      borderRadius: '8px',
      px: 2.5,
      py: 0.9,
      boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
      '&:hover': { bgcolor: '#059669' },
    }}
  >
    Exporter toutes les données (Excel)
  </Button>
</Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)',
          overflow: 'hidden',
        }}
      >
        
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>
                OFFRE
              </TableCell>
              <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>
                STATUT
              </TableCell>
              <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>
                REVENUS
              </TableCell>
              <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>
                Total Réservations
              </TableCell>

              <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>
                PERFORMANCE
              </TableCell>
             
            </TableRow>
          </TableHead>
          <TableBody>
           {offers.map((offre, index) => {
  const totalReservations =
    (offre.pending_reservations || 0) +
    (offre.successful_reservations || 0) +
    (offre.cancelled_reservations || 0);

  const getPercent = (value) => {
    if (!totalReservations) return 0;
    return Math.round((value / totalReservations) * 100);
  };

  return (
              <React.Fragment key={offre.offre_id}>
                <TableRow
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setOpenRow(openRow === index ? null : index)}
                >
                  <TableCell>
                    <IconButton size="small">
                      {openRow === index ? (
                        <KeyboardArrowUp />
                      ) : (
                        <KeyboardArrowDown />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        src={`${apiUrl}/storage/workshop/${offre.intervenant.photo}`}
                        sx={{ width: 45, height: 45 }}
                      />
                      <Box>
                        <Typography fontWeight={700}>{offre.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {offre.intervenant.prenom} {offre.intervenant.nom}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
              

<Stack direction="row" spacing={1}>
  <Chip
    label={`${offre.pending_reservations || 0} (${getPercent(offre.pending_reservations)}%) En attente`}
    size="small"
    sx={{ bgcolor: '#FEF3C7', color: '#92400E' }}
  />
  <Chip
    label={`${offre.successful_reservations || 0} (${getPercent(offre.successful_reservations)}%) Validés`}
    size="small"
    sx={{ bgcolor: '#DCFCE7', color: '#166534' }}
  />
  <Chip
    label={`${offre.cancelled_reservations || 0} (${getPercent(offre.cancelled_reservations)}%) Annulés`}
    size="small"
    sx={{ bgcolor: '#FFE4E6', color: '#DC2626' }}
  />
</Stack>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={800}>
                      {parseFloat(offre.revenue).toLocaleString()} EURO
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={800}>
                      {offre.total_reservations}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>
                    {(() => {
                      const total = offre.total_slots || 0;
                      const disponibles =
                        offre.slot_distribution?.available || 0;
                      const reserves = offre.slot_distribution?.reserved || 0;

                      const pourcentage =
                        total > 0 ? (reserves / total) * 100 : 0;

                      return (
                        <Box
                          sx={{
                            width: '100%',
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                          }}
                        >
                          {/* Ligne supérieure : Titre à gauche, Chiffres à droite */}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: '#4b5563',
                                letterSpacing: '0.5px',
                              }}
                            >
                              SLOTS
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: '1.2rem',
                                  fontWeight: 800,
                                  color: '#111827',
                                }}
                              >
                                {reserves}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.8rem',
                                  color: '#9ca3af',
                                  fontWeight: 500,
                                }}
                              >
                                / {total} réservés
                              </Typography>
                            </Box>
                          </Box>

                          {/* Barre de progression avec de l'espace au-dessus et en-dessous */}
                          <Box sx={{ py: 0.5 }}>
                            <LinearProgress
                              variant="determinate"
                              value={pourcentage}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: '#f3f4f6',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 3,
                                  bgcolor: '#3b82f6',
                                },
                              }}
                            />
                          </Box>

                          {/* Ligne inférieure : Slots dispos à gauche, Pourcentage à droite (sans retour à la ligne) */}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.8rem',
                                color: '#6b7280',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {disponibles} slots disponibles
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                color: '#3b82f6',
                              }}
                            >
                              {pourcentage.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })()}
                  </TableCell>

                  
                </TableRow>

                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 0, bgcolor: '#FAFAFA' }}>
                    <Collapse
                      in={openRow === index}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          p: 3,
                          bgcolor: '#ffffff',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 3,
                            fontWeight: 800,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: '#1e293b',
                          }}
                        >
                          <BarChart color="primary" /> Analyse & Activité (
                          {offre.total_slots} créneaux au total)
                        </Typography>

                        <Grid container spacing={4} alignItems="flex-start">
                          {/* COLONNE 1 : Statistiques */}
                          <Grid item xs={12} md={5.8}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                mb: 2,
                                fontWeight: 700,
                                color: '#64748b',
                                borderBottom: '2px solid #e2e8f0',
                                pb: 1,
                              }}
                            >
                              Statistiques des créneaux
                            </Typography>
                            <Stack spacing={2.5}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Taux d'occupation global
                                </Typography>
                                <Typography
                                  variant="h4"
                                  sx={{ fontWeight: 800, color: '#4f46e5' }}
                                >
                                  {offre.occupancy_rate}%
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={offre.occupancy_rate}
                                  sx={{
                                    mt: 1,
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: '#f1f5f9',
                                  }}
                                />
                              </Box>

                              <Stack spacing={1.5}>
                                {[
                                  {
                                    label: 'Disponible',
                                    val: offre.slot_distribution?.available,
                                    color: '#22c55e',
                                  },
                                  {
                                    label: 'Réservé',
                                    val: offre.slot_distribution?.reserved,
                                    color: '#f59e0b',
                                  },
                                  {
                                    label: 'Bloqué',
                                    val: offre.slot_distribution?.blocked,
                                    color: '#64748b',
                                  },
                                  {
                                    label: 'Annulé',
                                    val: offre.slot_distribution?.cancelled,
                                    color: '#ef4444',
                                  },
                                ].map((item) => (
                                  <Stack
                                    key={item.label}
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontWeight: 500,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: '50%',
                                          bgcolor: item.color,
                                          mr: 1.5,
                                        }}
                                      />{' '}
                                      {item.label}
                                    </Typography>
                                    <Stack
                                      direction="row"
                                      spacing={2}
                                      alignItems="center"
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#94a3b8',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {offre.total_slots > 0
                                          ? (
                                              (item.val / offre.total_slots) *
                                              100
                                            ).toFixed(1)
                                          : 0}
                                        %
                                      </Typography>
                                      <Typography
                                        fontWeight={700}
                                        sx={{
                                          minWidth: 35,
                                          textAlign: 'right',
                                        }}
                                      >
                                        {item.val}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                ))}
                              </Stack>
                            </Stack>
                          </Grid>

                          {/* SÉPARATEUR */}
                          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Divider
                              orientation="vertical"
                              flexItem
                              sx={{ height: '100%', ml: 2 }}
                            />
                          </Box>

                          {/* COLONNE 2 : Timeline */}
                          <Grid item xs={12} md={5.8}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                mb: 2,
                                fontWeight: 700,
                                color: '#64748b',
                                borderBottom: '2px solid #e2e8f0',
                                pb: 1,
                              }}
                            >
                              Dernières réservations
                            </Typography>
                            <Stack spacing={1.5}>
                              {offre.reservation_timeline?.length > 0 ? (
                                offre.reservation_timeline
                                  .slice(0, 3)
                                  .map((res) => (
                                    <Paper
                                      key={res.reservation_id}
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        borderRadius: 2,
                                        bgcolor: '#f8fafc',
                                      }}
                                    >
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          fontWeight={600}
                                          sx={{ color: '#1e293b' }}
                                        >
                                          {res.client.prenom} {res.client.name}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {new Date(
                                            res.start_date
                                          ).toLocaleDateString()}{' '}
                                          •{' '}
                                          {new Date(
                                            res.start_date
                                          ).toLocaleTimeString([], {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}
                                        </Typography>
                                      </Box>
                                      <Chip
                                        label={res.status}
                                        size="small"
                                        sx={{
                                          textTransform: 'capitalize',
                                          fontWeight: 600,
                                          bgcolor:
                                            res.status === 'approved'
                                              ? '#dcfce7'
                                              : res.status === 'cancelled'
                                                ? '#fee2e2'
                                                : '#fef3c7',
                                          color:
                                            res.status === 'approved'
                                              ? '#166534'
                                              : res.status === 'cancelled'
                                                ? '#991b1b'
                                                : '#92400e',
                                        }}
                                      />
                                    </Paper>
                                  ))
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontStyle: 'italic' }}
                                >
                                  Aucune réservation récente.
                                </Typography>
                              )}
                            </Stack>
                          </Grid>
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ display: { xs: 'none', md: 'block' } }}
                          />

                          {/* COLONNE 3 : Performance & Revenus (Nouvelle) */}
                          <Grid item xs={12} md={3.7}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                mb: 2,
                                fontWeight: 700,
                                color: '#64748b',
                                borderBottom: '2px solid #e2e8f0',
                                pb: 1,
                              }}
                            >
                              Performance financière
                            </Typography>
                            <Stack spacing={2}>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Revenu total
                                </Typography>
                                <Typography variant="h6" fontWeight={800}>
                                  {parseFloat(offre.revenue).toLocaleString()} €
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Taux de succès
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={offre.success_rate}
                                  sx={{ height: 6, borderRadius: 2, mt: 0.5 }}
                                />
                                <Typography variant="caption" fontWeight={700}>
                                  {offre.success_rate}%
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Panier moyen
                                </Typography>
                                <Typography variant="h6" fontWeight={700}>
                                  {(
                                    offre.revenue /
                                    (offre.successful_reservations || 1)
                                  ).toFixed(0)}{' '}
                                  €
                                </Typography>
                              </Box>
                            </Stack>
                          </Grid>
                        </Grid>
                      </Box>
                      <Box sx={{ p: 4 }}>
                        {/* 1. Section Top Clients */}
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 3,
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <StarOutline color="warning" /> Top Clients
                        </Typography>
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                          {offre.top_clients?.map((c) => (
                            <ClientCard key={`top-${c.client_id}`} client={c} />
                          ))}
                        </Grid>

                        <Divider sx={{ mb: 4, borderStyle: 'dashed' }} />

                        {/* 2. Section Liste Clients */}
                        <Button
                          variant={
                            expandedList[offre.offre_id]
                              ? 'outlined'
                              : 'contained'
                          }
                          onClick={() => toggleList(offre.offre_id)}
                          sx={{ mb: 3 }}
                        >
                          {expandedList[offre.offre_id]
                            ? 'Fermer la liste des clients'
                            : `Voir la liste des clients (${offre.clients?.length || 0})`}
                        </Button>

                        <Collapse in={expandedList[offre.offre_id]}>
                          <Grid container spacing={3} sx={{ mb: 4 }}>
                            {offre.clients
                              ?.slice(0, limits[offre.offre_id] || 5)
                              .map((c) => (
                                <ClientCard
                                  key={`all-${c.client_id}`}
                                  client={c}
                                />
                              ))}
                          </Grid>
                          {(limits[offre.offre_id] || 5) <
                            (offre.clients?.length || 0) && (
                            <Box sx={{ mb: 4, textAlign: 'center' }}>
                              <Button
                                variant="outlined"
                                onClick={() => handleLoadMore(offre.offre_id)}
                              >
                                Voir 5 clients de plus
                              </Button>
                            </Box>
                          )}
                        </Collapse>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
                
              </React.Fragment>
);
})}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
